<template>
  <div class="service_update" v-if="driverServiceVersion.isShowServiceUpdate.value && lastServiceInfo">
    <div class="service_update_box">
      <div class="service_update_title">
        <div
          class="upgrade_icon"
          :class="{
            upgrade: updateStatus == VersionStatus.NoUpdate || updateStatus == VersionStatus.Upgrading,
            success: updateStatus == VersionStatus.Success,
            error: updateStatus == VersionStatus.Error,
          }"
        ></div>

        <span v-if="updateStatus == VersionStatus.NoUpdate">{{ $t('new_version') }}</span>
        <span v-else-if="updateStatus == VersionStatus.Upgrading">{{ $t('upgrading') }}</span>
        <span v-else-if="updateStatus == VersionStatus.Error">{{ $t('upgrade_error') }}</span>
        <span v-else-if="updateStatus == VersionStatus.Success">{{ $t('upgrade_success') }}</span>
      </div>
      <div class="service_update_des" v-if="updateStatus == VersionStatus.NoUpdate">{{ $t('new_version_des') }}</div>
      <div class="service_update_des" v-else-if="updateStatus == VersionStatus.Upgrading">{{ $t('new_version_des1') }}</div>
      <div class="service_update_des" v-else-if="updateStatus == VersionStatus.Error">{{ $t('new_version_des2') }}</div>
      <div class="service_update_info" v-if="updateStatus == VersionStatus.NoUpdate || updateStatus == VersionStatus.Success">
        <template v-if="updateStatus == VersionStatus.NoUpdate">
          <div>{{ $t('update_info') }}</div>
          <textarea disabled="false">{{ lastServiceInfo?.UpdateInfo }}</textarea>
        </template>
        <template v-if="updateStatus == VersionStatus.Success">
          <div>{{ $t('current_version') }}：{{ DeviceManager.instance.state.driverServiceVersion }}</div>
        </template>
      </div>
      <div class="progress_box" v-if="updateStatus == VersionStatus.Upgrading">
        <progress class="progress" :max="serviceProgressData?.Max" :value="serviceProgressData?.Value"></progress>
      </div>
      <div class="button update enable" v-if="updateStatus == VersionStatus.NoUpdate" @click="updateServiceVersion">{{ $t('update') }}</div>
      <div class="button update enable" v-else-if="updateStatus == VersionStatus.Error" @click="updateServiceVersion">{{ $t('re_update') }}</div>
      <div class="button update enable" v-else-if="updateStatus == VersionStatus.Success" @click="updateSuccessClick">
        {{ $t('confirm') }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
interface IVersion {
  Finished: boolean
  Max: number
  Speed: String
  Value: number
}
import { HelperService } from '@/service/helper/helper'
import { HelperSocketMsg, HelperSocketMsgType } from '@/service/helper/message'
import { driverServiceVersion, VersionStatus } from '@/upgrade/service'
import { DeviceManager } from '@/service/device'
import { onMounted, ref, watch } from 'vue'
import { IVersionInfo, VersionSource } from '@/service/response'

const lastServiceInfo = ref<IVersionInfo>()
const serviceProgressData = ref<IVersion>()
const updateStatus = ref(VersionStatus.NoUpdate)

watch(updateStatus, (value: VersionStatus) => {
  driverServiceVersion.serviceUpdateVersionStatus = value
})
onMounted(() => {
  handleUpdateReportProgress()
  handleLatestVersion()
})

const handleLatestVersion = () => {
  HelperService.instance.socketService.setHandler(HelperSocketMsgType.MsgTypeLatestVersion, (info: IVersionInfo) => {
    if (info.ID == VersionSource.driver_service) {
      lastServiceInfo.value = info
    }
  })
}

const handleUpdateReportProgress = () => {
  HelperService.instance.socketService.setHandler(
    HelperSocketMsgType.MsgTypeReportProgress,
    (msg: IVersion) => {
      updateStatus.value = VersionStatus.Upgrading
      serviceProgressData.value = msg
      if (msg.Finished) {
        updateStatus.value = VersionStatus.Success
      }
    },
    (e) => {
      console.error('升级失败', e)
      updateStatus.value = VersionStatus.Error
    },
  )
}

const updateServiceVersion = async () => {
  if (!lastServiceInfo.value) {
    updateStatus.value = VersionStatus.Error
    console.error('请先获取最新版本信息')
    return
  }

  if (updateStatus.value == VersionStatus.Upgrading) return

  // 只对比前两位版本号 （将.VersionName 前两位保留，第3位为0）
  const v = lastServiceInfo.value.VersionName
  const version = v.split('.')[0] + '.' + v.split('.')[1] + '.0'
  const launch = {
    Type: HelperSocketMsgType.MsgTypeInstallVersion,
    Data: version,
  } as HelperSocketMsg<any>
  updateStatus.value = VersionStatus.Upgrading
  HelperService.instance.socketService.ask(launch).catch((err) => {
    updateStatus.value = VersionStatus.Error
    console.error('开始升级：升级失败', err)
  })
}

const updateSuccessClick = () => {
  driverServiceVersion.isShowServiceUpdate.value = false
  updateStatus.value = VersionStatus.NoUpdate
}
</script>

<style lang="less" scoped>
.service_update {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10;
  // -webkit-app-region: no-drag;
  background-color: rgba(#000000, 0.2);
  // cursor: not-allowed;

  .service_update_box {
    background-color: #fff;
    padding: 50px 50px 32px 50px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    box-shadow: 0 8px 16px rgba(#393939, 0.2);
  }

  .service_update_title {
    display: flex;
    align-items: center;
    justify-content: center;

    .upgrade_icon {
      width: 32px;
      height: 24px;
      background-size: 100% 100%;

      &.upgrade {
        background-image: url('@/assets/img/upgrade.svg');
      }

      &.success {
        background-image: url('@/assets/img/upgrade_success.svg');
      }

      &.error {
        background-image: url('@/assets/img/upgrade_err.svg');
      }
    }

    span {
      font-size: 22px;
      font-weight: bold;
      color: var(--brand-color-pure);
      margin-left: 10px;
    }
  }

  .service_update_des {
    font-size: 16px;
    margin-top: 30px;
    color: #666;
  }

  .service_update_info {
    background-color: #f6f6f6;
    border-radius: var(--radius-l);
    min-width: 426px;
    padding: 20px 20px 12px 20px;
    margin-top: 14px;
    font-size: 14px;
    color: #888;
    line-height: 1.5;
    height: 112px;

    textarea {
      border: 0 solid transparent;
      font-size: 14px;
      color: #888;
      line-height: 1.5;
      background-color: transparent;
      width: 100%;
    }
  }

  .progress_box {
    height: 10px;
    margin-top: 30px;
    width: 370px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid var(--brand-color-pure);
    border-radius: var(--action-btn-radius);
    padding: 2px;
    background: rgba(255, 255, 255, 0.1);

    progress {
      height: 100%;
      width: 100%;
      border-radius: var(--action-btn-radius);
      overflow: hidden;

      &::-webkit-progress-bar {
        background-color: transparent;
      }

      &::-webkit-progress-value {
        background: var(--brand-color) no-repeat;
        border-radius: var(--action-btn-radius);
      }

      &::-moz-progress-bar {
        background-color: #f3f3f3;
      }
    }

    span {
      margin-left: 20px;
      text-wrap: nowrap;
    }
  }

  .update {
    // width: 100px;
    padding: 12px 24px;
    margin-top: 20px;
  }
}
</style>
